import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';

const { FiSend, FiMessageSquare, FiPlus, FiClock, FiCheckCircle, FiFileText } = FiIcons;

const SupportSystem = ({ user, isAdmin = false }) => {
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [newTicketSubject, setNewTicketSubject] = useState('');
  const [newTicketBody, setNewTicketBody] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.id) fetchTickets();
  }, [user?.id]);

  useEffect(() => {
    if (selectedTicket?.id) fetchMessages(selectedTicket.id);
  }, [selectedTicket?.id]);

  const fetchTickets = async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      let query = supabase
        .from('app_tickets')
        .select('*, app_profiles(full_name, company_name, email)');

      if (!isAdmin) {
        query = query.eq('user_id', user.id);
      } else {
        query = query.order('status', { ascending: true });
      }

      const { data, error } = await query.order('updated_at', { ascending: false });
      if (error) throw error;
      setTickets(data || []);
    } catch (err) {
      console.error("Error fetching tickets:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (ticketId) => {
    try {
      const { data, error } = await supabase
        .from('app_ticket_messages')
        .select('*')
        .eq('ticket_id', ticketId)
        .order('created_at', { ascending: true });
      
      if (error) throw error;
      setMessages(data || []);
    } catch (err) {
      console.error("Error fetching messages:", err);
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (!user?.id || !newTicketSubject.trim() || !newTicketBody.trim()) return;

    try {
      const { data: ticket, error } = await supabase
        .from('app_tickets')
        .insert({
          user_id: user.id,
          subject: newTicketSubject,
          description: newTicketBody,
          status: 'open'
        })
        .select()
        .single();

      if (error) throw error;

      if (ticket) {
        await supabase.from('app_ticket_messages').insert({
          ticket_id: ticket.id,
          sender_id: user.id,
          message: newTicketBody
        });
        
        setNewTicketSubject('');
        setNewTicketBody('');
        setIsCreating(false);
        fetchTickets();
        setSelectedTicket(ticket);
      }
    } catch (err) {
      console.error("Erro ao criar ticket:", err);
      alert("Erro ao abrir chamado.");
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedTicket?.id || !user?.id) return;

    try {
      const { error: msgError } = await supabase.from('app_ticket_messages').insert({
        ticket_id: selectedTicket.id,
        sender_id: user.id,
        message: newMessage
      });

      if (msgError) throw msgError;

      const newStatus = isAdmin ? 'replied' : 'open';
      await supabase.from('app_tickets').update({ status: newStatus, updated_at: new Date() }).eq('id', selectedTicket.id);

      setNewMessage('');
      fetchMessages(selectedTicket.id);
      fetchTickets();
    } catch (err) {
      console.error("Erro ao enviar mensagem:", err);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 min-h-[600px] h-[calc(100vh-250px)]">
      <div className="bg-white rounded-[32px] border border-slate-100 shadow-sm overflow-hidden flex flex-col h-full">
        <div className="p-6 bg-slate-50 border-b flex justify-between items-center">
          <div>
            <h3 className="font-black text-slate-800 uppercase italic text-sm">Central de Ajuda</h3>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Suporte Técnico</p>
          </div>
          {!isAdmin && (
            <button onClick={() => { setIsCreating(true); setSelectedTicket(null); }} className="p-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all shadow-lg shadow-blue-100">
              <SafeIcon icon={FiPlus} />
            </button>
          )}
        </div>
        <div className="flex-1 overflow-y-auto divide-y divide-slate-50">
          {loading ? (
            <div className="p-10 text-center animate-pulse text-slate-400 font-bold uppercase text-[10px]">Carregando...</div>
          ) : (
            tickets.map(ticket => (
              <button 
                key={ticket.id} 
                onClick={() => { setSelectedTicket(ticket); setIsCreating(false); }}
                className={`w-full p-6 text-left hover:bg-slate-50 transition-all group ${selectedTicket?.id === ticket.id ? 'bg-blue-50/50 border-l-4 border-l-blue-600' : 'border-l-4 border-l-transparent'}`}
              >
                <div className="flex justify-between items-start mb-2">
                  <span className="font-bold text-slate-900 text-sm truncate pr-2 uppercase italic">{ticket.subject}</span>
                  <span className={`text-[8px] font-black uppercase px-2 py-1 rounded-lg ${
                    ticket.status === 'open' ? 'bg-yellow-100 text-yellow-700' : 
                    ticket.status === 'replied' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'
                  }`}>
                    {ticket.status === 'open' ? 'Aberto' : ticket.status === 'replied' ? 'Respondido' : 'Fechado'}
                  </span>
                </div>
                <div className="flex items-center text-[9px] text-slate-400 font-bold uppercase tracking-wider">
                  <SafeIcon icon={FiClock} className="mr-1" />
                  {new Date(ticket.updated_at).toLocaleDateString()}
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      <div className="lg:col-span-2 bg-white rounded-[32px] border border-slate-100 shadow-sm overflow-hidden flex flex-col h-full">
        {isCreating ? (
          <div className="p-10 flex flex-col h-full justify-center max-w-lg mx-auto w-full overflow-y-auto">
            <h3 className="text-3xl font-black text-slate-900 mb-2 uppercase italic">Novo Chamado</h3>
            <p className="text-slate-500 text-sm mb-8 font-medium italic">Preencha os detalhes do seu problema.</p>
            <form onSubmit={handleCreateTicket} className="space-y-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase text-slate-400 ml-1">Assunto</label>
                <input 
                  type="text" placeholder="Resumo do problema" value={newTicketSubject} 
                  onChange={(e) => setNewTicketSubject(e.target.value)}
                  className="w-full p-5 bg-slate-50 border border-transparent rounded-2xl outline-none focus:ring-2 focus:ring-blue-500 font-bold text-slate-900"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase text-slate-400 ml-1">Mensagem Detalhada</label>
                <textarea 
                  rows="4" placeholder="Descreva aqui o que está acontecendo..." value={newTicketBody} 
                  onChange={(e) => setNewTicketBody(e.target.value)}
                  className="w-full p-5 bg-slate-50 border border-transparent rounded-2xl outline-none focus:ring-2 focus:ring-blue-500 font-bold text-slate-900 resize-none"
                  required
                />
              </div>
              <div className="flex space-x-4">
                <button type="submit" className="flex-1 bg-blue-600 text-white py-4 rounded-2xl font-black uppercase italic shadow-xl shadow-blue-100 hover:bg-blue-700">Enviar Chamado</button>
                <button type="button" onClick={() => setIsCreating(false)} className="px-8 bg-slate-100 text-slate-500 rounded-2xl font-black uppercase hover:bg-slate-200 transition-all">Sair</button>
              </div>
            </form>
          </div>
        ) : selectedTicket ? (
          <>
            <div className="p-6 bg-slate-50 border-b flex justify-between items-center">
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm border border-slate-100">
                  <SafeIcon icon={FiMessageSquare} className="text-blue-600" />
                </div>
                <div>
                  <h3 className="font-black text-slate-800 uppercase italic text-sm">{selectedTicket.subject}</h3>
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest">Ticket #{selectedTicket.id.slice(0,8)}</p>
                </div>
              </div>
              <button 
                onClick={async () => {
                  if (window.confirm("Deseja encerrar este chamado?")) {
                    await supabase.from('app_tickets').update({ status: 'closed' }).eq('id', selectedTicket.id);
                    fetchTickets();
                    setSelectedTicket(null);
                  }
                }}
                className="flex items-center space-x-2 text-[9px] font-black uppercase text-red-500 hover:bg-red-50 px-4 py-2 rounded-xl transition-all"
              >
                <SafeIcon icon={FiCheckCircle} />
                <span>Encerrar</span>
              </button>
            </div>
            
            <div className="flex-1 p-8 overflow-y-auto space-y-6 bg-slate-50/20">
              {messages.map(msg => (
                <div key={msg.id} className={`flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] p-5 rounded-3xl text-sm font-bold shadow-sm ${
                    msg.sender_id === user?.id ? 'bg-blue-600 text-white rounded-tr-none shadow-blue-100' : 'bg-white border border-slate-100 text-slate-700 rounded-tl-none'
                  }`}>
                    <p className="leading-relaxed whitespace-pre-wrap">{msg.message}</p>
                    <div className="text-[8px] mt-2 font-black uppercase opacity-60 flex items-center">
                      <SafeIcon icon={FiClock} className="mr-1" />
                      {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <form onSubmit={handleSendMessage} className="p-6 border-t bg-white flex space-x-4">
              <input 
                type="text" value={newMessage} onChange={(e) => setNewMessage(e.target.value)} 
                placeholder="Sua resposta..." 
                className="flex-1 p-5 bg-slate-50 rounded-2xl outline-none border border-transparent focus:border-blue-200 font-bold text-slate-900"
                required
              />
              <button type="submit" className="p-5 bg-slate-900 text-white rounded-2xl shadow-xl hover:bg-slate-800 transition-all">
                <SafeIcon icon={FiSend} />
              </button>
            </form>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center p-10 text-slate-300">
            <SafeIcon icon={FiMessageSquare} className="text-5xl opacity-10 mb-4" />
            <p className="font-black uppercase italic text-xs tracking-[0.2em] text-slate-400">Selecione um chamado</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupportSystem;